import React, { useCallback, useEffect, useState } from 'react'
import styles from './RaiseBar.module.css'
import useTableSettings from '@/src/hooks/data/useTableSettings';
import { 
  useMinRaiseAmount,
  useMaxRaiseAmount,
  useGameType,
  useGameDefinition,
  useTotalPotAmount,
  useGameStage
} from '@/src/hooks/data/useGameTable';
import useMyPlayerSeat from '@/src/hooks/data/useMyPlayerSeat';
import { parseDecimalValuesOfNumber } from '../../../Common/utils/GameUtils';
import { GAME_STAGE_PRE_FLOP, OMAHA, OMAHA5, OMAHA6 } from '@/Poker/data/Constants';

const RAISE_PRESET_ALL_IN = "All In";
// const RAISE_2X_POT = "2x Pot";

export default function RaisePresets({ tempTableId, raiseAmount, setRaiseAmount }) {
    const [raisePresets, setRaisePresets] = useState([]);
    const [selectedPreset, setSelectedPreset] = useState(null);
    const minRaiseAmount = useMinRaiseAmount(tempTableId);
    const maxRaiseAmount = useMaxRaiseAmount(tempTableId);
    const gameType = useGameType(tempTableId);
    const gameDefinition = useGameDefinition(tempTableId);
    const bigBlind = gameDefinition?.bigBlind;
    const totalPotAmt = useTotalPotAmount(tempTableId);
    const gameStage = useGameStage(tempTableId);
    const { betAmount, atTableAmount } = useMyPlayerSeat(tempTableId);
    const { tableSettings } = useTableSettings();
    const { bettingOptions } = tableSettings;


    const updateRaisePresets = useCallback(() => {
        try {
            // Extract user preferences
            const {
                defaultPreflopPresetValue,
                defaultPostflopPresetValue,
                preflopPresetValues,
                postflopPresetValues,
            } = bettingOptions;

            let defaultRaiseValue = minRaiseAmount;

            let maxLimit =
                gameType === OMAHA ||
                    gameType === OMAHA5 ||
                    gameType === OMAHA6
                    ? maxRaiseAmount
                    : atTableAmount;

            let raisePresetsValues = [];

            if (gameStage === GAME_STAGE_PRE_FLOP) {
                raisePresetsValues = Object.entries(preflopPresetValues)
                    .map(([key, value]) => {
                        const presetValue = bigBlind * value;
                        const isEnabled = maxLimit >= presetValue && presetValue >= minRaiseAmount;
                        return {
                            label: `${value}x BB`,
                            value: presetValue,
                            isEnabled: isEnabled,
                        };
                    })
                    .filter(
                        (preset, index, self) =>
                            index === self.findIndex((p) => p.value === preset.value) // Filter out duplicate values
                    )
                    .sort((a, b) => b.value - a.value); // Sort by value in descending order

                // Add the 'All In' option manually
                raisePresetsValues.unshift({
                    label: RAISE_PRESET_ALL_IN,
                    value: atTableAmount + betAmount,
                    isEnabled: true,
                });

                let defaultValue = defaultPreflopPresetValue * bigBlind;

                // New condition for OMAHA, OMAHA5, OMAHA6
                if (
                    (gameType === OMAHA ||
                        gameType === OMAHA5 ||
                        gameType === OMAHA6) &&
                    defaultPreflopPresetValue > maxRaiseAmount
                ) {
                    defaultRaiseValue = maxRaiseAmount;
                } else if (
                    defaultValue > minRaiseAmount &&
                    defaultValue <= maxRaiseAmount
                ) {
                    defaultRaiseValue = defaultValue;
                }
            } else {
                raisePresetsValues = Object.entries(postflopPresetValues)
                    .map(([key, value]) => {
                        const presetValue = totalPotAmt * value;
                        const isEnabled = maxLimit >= presetValue && presetValue >= minRaiseAmount && presetValue >= bigBlind;
                        return {
                            label: `${value}x Pot`,
                            value: presetValue,
                            isEnabled: isEnabled,
                        };
                    })
                    .filter(
                        (preset, index, self) =>
                            index === self.findIndex((p) => p.value === preset.value) // Filter out duplicate values
                    )
                    .sort((a, b) => b.value - a.value); // Sort by value in descending order

                // Add the 'All In' option manually
                raisePresetsValues.unshift({
                    label: RAISE_PRESET_ALL_IN,
                    value: maxLimit + betAmount,
                    isEnabled: true,
                });

                let defaultValue = defaultPostflopPresetValue * bigBlind;

                // Validate default raise value
                if (
                    defaultValue > minRaiseAmount &&
                    defaultValue <= maxRaiseAmount
                ) {
                    defaultRaiseValue = defaultValue;
                }
            }

            // Ensure defaultRaiseValue is valid
            if (
                defaultRaiseValue < minRaiseAmount ||
                defaultRaiseValue > maxRaiseAmount
            ) {
                defaultRaiseValue = minRaiseAmount;
            }

            let raiseActionBetValue = parseDecimalValuesOfNumber(minRaiseAmount);

            if (minRaiseAmount >= maxRaiseAmount) {
                defaultRaiseValue = Number(parseDecimalValuesOfNumber(maxRaiseAmount));
                raiseActionBetValue = parseDecimalValuesOfNumber(
                    Number(parseDecimalValuesOfNumber(maxRaiseAmount))
                );
            }

            if (raiseActionBetValue) {
                // props.handleRaiseActionBet(raiseActionBetValue);
            }
            if (defaultRaiseValue) {
                setRaiseAmount(defaultRaiseValue);
                // props.handleRaiseActionBet(defaultRaiseValue);
            }
            // console.log('Spruha log >>> Raise Presets >>> ', raisePresetsValues);
            setRaisePresets(raisePresetsValues); // Set the filtered and sorted presets
        } catch (error) {
            // console.error('Spruha log >>> Error in updateRaisePresets >>> ', error);
        }
    }, [betAmount, atTableAmount, bettingOptions, gameStage, gameType, bigBlind, totalPotAmt, minRaiseAmount, maxRaiseAmount, setRaiseAmount]);

    useEffect(() => {
        if (bettingOptions) {
            updateRaisePresets();
        }
    }, [betAmount, atTableAmount, bettingOptions, updateRaisePresets])

    // Handle preset selection
    const handlePresetClick = (preset) => {
        setRaiseAmount(preset.value);
        setSelectedPreset(preset);
    };

    // Clear selected preset when raiseAmount changes (indicating manual slider adjustment)
    useEffect(() => {
        if (selectedPreset && Math.abs(raiseAmount - selectedPreset.value) > 0.01) {
            setSelectedPreset(null);
        }
    }, [raiseAmount, selectedPreset]);

    return (
        <div className={styles.raiseBarBettingPresets}>
            {raisePresets
                .filter(preset => preset.isEnabled)
                .map((preset) => {
                    const isSelected = selectedPreset && selectedPreset.value === preset.value;
                    return (
                        <div 
                            key={'Raise Preset- ' + preset.label + ' - ' + preset.value} 
                            className={styles.raiseBarBettingPreset} 
                            onClick={() => handlePresetClick(preset)}
                            style={{
                                backgroundColor: isSelected ? 'var(--color-background)' : undefined,
                                color: isSelected ? 'var(--color-accent-theme)' : undefined
                            }}
                        >
                            {preset.label}
                        </div>
                    );
                })}
        </div>
    )
}
